import { type ReactNode, useEffect, useRef, useState } from 'react';
import { type Bubble } from '@/utils/bubble-physics';
import { readBubbleAnchors } from '@/utils/bubble-anchors';
import { spawnPositions } from '@/utils/bubble-poisson';
import { useHomeBubbles, type BubbleItem } from '@/hooks/use-home-bubbles';
import { useBubblePhysics } from './use-bubble-physics';
import { buildBubbleSeeds } from './bubble-depth';
import { MAX_BUBBLE_SIZE, Slot, createBubbles } from './floating-bubbles-parts';

// If the photos endpoint hangs or fails, give up waiting after this
// long and spawn with whatever we have (likely zero photos → all 🤑).
const SPAWN_FALLBACK_MS = 1500;

function spawnNow(c: HTMLDivElement, photos: BubbleItem[]): Bubble[] {
  const seeds = buildBubbleSeeds(c.clientWidth, c.clientHeight);
  const positions = spawnPositions({
    width: c.clientWidth,
    height: c.clientHeight,
    size: MAX_BUBBLE_SIZE,
    rects: readBubbleAnchors(c),
    seeds,
  });
  return createBubbles(photos, positions, seeds);
}

// Spawn exactly once. The previous version had `photos` in its deps,
// so the effect re-fired when the photo fetch resolved → an entire
// fresh set of 140 bubbles at new random positions replaced the
// pre-fetch set, which the user saw as a "everything jumps / re-
// generates" flicker. Now: gate the single spawn on either
// `!isLoading` (data settled, success or error) or a 1.5s safety
// timeout. After the spawn, photo updates are ignored.
type SpawnGate = {
  c: HTMLDivElement;
  spawnedRef: React.MutableRefObject<boolean>;
  photosRef: React.MutableRefObject<BubbleItem[]>;
  setBubbles: (b: Bubble[]) => void;
};

function tryRunSpawn(g: SpawnGate): void {
  if (g.spawnedRef.current) return;
  g.spawnedRef.current = true;
  g.setBubbles(spawnNow(g.c, g.photosRef.current));
}

function useBubbles(
  photos: BubbleItem[],
  isLoading: boolean,
  containerRef: React.RefObject<HTMLDivElement | null>
): Bubble[] {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const spawnedRef = useRef(false);
  const photosRef = useRef(photos);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);
  useEffect(() => {
    const c = containerRef.current;
    if (!c || spawnedRef.current) return;
    const gate = { c, spawnedRef, photosRef, setBubbles };
    if (!isLoading) return tryRunSpawn(gate);
    const id = globalThis.setTimeout(
      () => tryRunSpawn(gate),
      SPAWN_FALLBACK_MS
    );
    return () => globalThis.clearTimeout(id);
  }, [isLoading, containerRef]);
  return bubbles;
}

type SlotsProps = {
  bubbles: Bubble[];
  elementRefs: React.RefObject<(HTMLDivElement | null)[]>;
};

function Slots({ bubbles, elementRefs }: SlotsProps): ReactNode {
  return (
    <>
      {bubbles.map((b, i) => (
        <Slot
          key={i}
          bubble={b}
          idx={i}
          refCb={(el) => {
            elementRefs.current[i] = el;
          }}
        />
      ))}
    </>
  );
}

export function FloatingBubbles(): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null);
  const elementRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { data, isLoading } = useHomeBubbles();
  const photos = data?.items ?? [];
  const bubbles = useBubbles(photos, isLoading, containerRef);
  useBubblePhysics({ containerRef, elementRefs, bubbles });
  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
    >
      <Slots bubbles={bubbles} elementRefs={elementRefs} />
    </div>
  );
}
